import React, { useState } from 'react';
import { Store, Download, Star, CheckCircle } from 'lucide-react';
import { AgentTemplate, CloneTemplateRequest } from '../../../services/api';
import TemplateDetailModal from '../TemplateDetailModal/TemplateDetailModal';
import styles from './TemplatesList.module.css';

export interface TemplatesListProps {
  templates: AgentTemplate[];
  isLoading?: boolean;
  onClone: (template: AgentTemplate) => void;
  searchQuery?: string;
}

export const TemplatesList: React.FC<TemplatesListProps> = ({
  templates,
  isLoading = false,
  onClone,
  searchQuery,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cloning, setCloning] = useState(false);

  const handleTemplateClick = (template: AgentTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloneFromModal = async (template: AgentTemplate, customization: CloneTemplateRequest) => {
    setCloning(true);
    try {
      await onClone(template);
      setIsModalOpen(false);
    } finally {
      setCloning(false);
    }
  };

  const getRating = (template: AgentTemplate): number => {
    return template.rating || template.averageRating || 0;
  };

  const getCloneCount = (template: AgentTemplate): number => {
    return template.cloneCount || template.totalClones || 0;
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star
          key={i}
          size={14}
          className={i < fullStars ? styles.starFilled : styles.starEmpty}
          fill={i < fullStars ? '#f59e0b' : 'none'}
        />
      );
    }
    return stars;
  };

  // Filter templates by search query
  const filteredTemplates = searchQuery
    ? templates.filter(
        (t) =>
          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : templates;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Store size={20} className={styles.headerIcon} />
          <h2 className={styles.count}>{filteredTemplates.length} MARKETPLACE TEMPLATES</h2>
        </div>
        <p className={styles.headerSubtitle}>
          Clone and customize pre-built AI agents for your team
        </p>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading templates...</span>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className={styles.empty}>
          <Store size={48} className={styles.emptyIcon} />
          <h3>No templates found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className={styles.card}
              onClick={() => handleTemplateClick(template)}
            >
              <div className={styles.cardHeader}>
                <div className={styles.cardAvatar}>
                  <span className={styles.avatarIcon}>{template.icon || '📋'}</span>
                </div>
                <div className={styles.cardInfo}>
                  <h3 className={styles.cardName}>{template.name}</h3>
                  <span className={styles.cardAuthor}>by {template.author || 'BrainIQ'}</span>
                </div>
                {(template.verified || template.isVerified) && (
                  <CheckCircle size={16} className={styles.verifiedIcon} />
                )}
              </div>

              <p className={styles.cardDescription}>
                {template.shortDescription || template.description?.substring(0, 120)}
                {template.description && template.description.length > 120 ? '...' : ''}
              </p>

              <div className={styles.cardTags}>
                {template.tags?.slice(0, 3).map((tag) => (
                  <span key={tag} className={styles.tag}>{tag}</span>
                ))}
                {template.tags && template.tags.length > 3 && (
                  <span className={styles.tagMore}>+{template.tags.length - 3}</span>
                )}
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.cardStats}>
                  <div className={styles.rating}>
                    {renderStars(getRating(template))}
                    <span className={styles.ratingValue}>{getRating(template).toFixed(1)}</span>
                  </div>
                  <div className={styles.clones}>
                    <Download size={14} />
                    <span>{getCloneCount(template)} clones</span>
                  </div>
                </div>
                <div className={styles.cardCapabilities}>
                  <span>{template.capabilities?.length || 0} capabilities</span>
                </div>
              </div>

              {(template.featured || template.isFeatured) && (
                <div className={styles.featuredBadge}>
                  <Star size={12} fill="#f59e0b" />
                  Featured
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onClone={handleCloneFromModal}
          cloning={cloning}
        />
      )}
    </div>
  );
};

export default TemplatesList;
